import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  CustomFieldOption,
  CustomFieldOptionField,
} from "../../../../types/client.type";
import {
  CUSTOM_OPTION_FIELDS,
  DEFAULT_EVENT_FIELD_OPTIONS,
  DUPLICATE_OPTION_MESSAGE,
  getCustomOptions,
  isDuplicateOption,
  setCustomOptions,
  sortOptionLabels,
} from "../../../../utils/eventFieldOptions";
import { Input } from "../../../Input";

type Props = {
  customFieldOptions: CustomFieldOption[];
  onChange: (customFieldOptions: CustomFieldOption[]) => void;
  disabled?: boolean;
};

const headerCellStyle = { fontWeight: "bold" } as const;

const defaultLabels = (field: CustomFieldOptionField) =>
  sortOptionLabels(DEFAULT_EVENT_FIELD_OPTIONS[field].map((o) => o.label));

export default function CustomOptionsTab({
  customFieldOptions,
  onChange,
  disabled,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [field, setField] = useState<CustomFieldOptionField>(
    CUSTOM_OPTION_FIELDS[0].field
  );
  const [options, setOptions] = useState<string[]>([]);
  const [optionInput, setOptionInput] = useState("");
  const [optionError, setOptionError] = useState<string | null>(null);

  const selectField = (newField: CustomFieldOptionField) => {
    setField(newField);
    setOptions([...getCustomOptions(customFieldOptions, newField)]);
    setOptionInput("");
    setOptionError(null);
  };

  const handleOpen = (newField: CustomFieldOptionField) => {
    selectField(newField);
    setDialogOpen(true);
  };

  const handleDelete = (deletedField: CustomFieldOptionField) => {
    onChange(setCustomOptions(customFieldOptions, deletedField, []));
  };

  const handleAddOption = () => {
    const option = optionInput.trim();
    if (!option) {
      setOptionError("Preencha a opção antes de adicionar");
      return;
    }
    if (isDuplicateOption(field, options, option)) {
      setOptionError(DUPLICATE_OPTION_MESSAGE);
      return;
    }
    setOptions([...options, option]);
    setOptionInput("");
    setOptionError(null);
  };

  const handleSave = () => {
    if (options.length === 0) {
      setOptionError("Adicione ao menos uma opção");
      return;
    }
    onChange(setCustomOptions(customFieldOptions, field, options));
    setDialogOpen(false);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} py={1}>
      <Alert
        severity="info"
        action={
          <Button
            variant="contained"
            disabled={disabled}
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpen(CUSTOM_OPTION_FIELDS[0].field)}
            style={{
              background: "#1361A4",
              color: "#FFFFFF",
              textTransform: "none",
              fontSize: 14,
              whiteSpace: "nowrap",
            }}
          >
            Adicionar Opção Personalizada
          </Button>
        }
        sx={{ alignItems: "center" }}
      >
        Configure opções adicionais para campos dropdown de Dados Cadastrais.
        <br />
        As opções personalizadas são somadas às opções padrão e valem apenas
        para este cliente.
      </Alert>

      <Typography fontWeight="bold" fontSize={16}>
        Opções Personalizadas
      </Typography>

      <TableContainer
        style={{
          boxShadow: "0px 0px 2px 1px #C4C4C4",
          borderRadius: 4,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ ...headerCellStyle, width: 200 }}>
                Campo (Dados Cadastrais)
              </TableCell>
              <TableCell style={headerCellStyle}>
                Opções padrão (ordem alfabética)
              </TableCell>
              <TableCell style={headerCellStyle}>
                Opções personalizadas (ordem alfabética)
              </TableCell>
              <TableCell style={{ ...headerCellStyle, width: 100 }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {CUSTOM_OPTION_FIELDS.map(({ field: rowField, label }) => {
              const custom = sortOptionLabels(
                getCustomOptions(customFieldOptions, rowField)
              );
              return (
                <TableRow key={rowField}>
                  <TableCell>{label}</TableCell>
                  <TableCell>{defaultLabels(rowField).join(", ")}</TableCell>
                  <TableCell>
                    {custom.length === 0 ? (
                      <Typography fontSize={14} color="textSecondary">
                        -
                      </Typography>
                    ) : (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {custom.map((option) => (
                          <Chip
                            key={option}
                            label={option}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Editar">
                        <span>
                          <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={() => handleOpen(rowField)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Excluir opções personalizadas">
                        <span>
                          <IconButton
                            size="small"
                            disabled={disabled || custom.length === 0}
                            onClick={() => handleDelete(rowField)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adicionar Opção Personalizada</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Input
              select
              marginTop={0}
              size="small"
              label="Campo (Dados Cadastrais) *"
              value={field}
              onChange={(e) =>
                selectField(e.target.value as CustomFieldOptionField)
              }
            >
              {CUSTOM_OPTION_FIELDS.map((option) => (
                <MenuItem key={option.field} value={option.field}>
                  {option.label}
                </MenuItem>
              ))}
            </Input>

            <Box p={1.5} borderRadius={1} bgcolor="#F5F7FA">
              <Typography fontSize={14} mb={1}>
                Opções padrão (ordem alfabética)
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {defaultLabels(field).map((option) => (
                  <Chip key={option} label={option} size="small" />
                ))}
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              <Typography fontWeight="bold" fontSize={14}>
                Opções personalizadas (cliente) *
              </Typography>
              <Input
                marginTop={0}
                size="small"
                value={optionInput}
                placeholder="Digite uma opção e pressione Enter"
                onChange={(e) => {
                  setOptionInput(e.target.value);
                  if (optionError) setOptionError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
                error={!!optionError}
                helperText={optionError ?? undefined}
              />
              {options.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {sortOptionLabels(options).map((option) => (
                    <Chip
                      key={option}
                      label={option}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={() =>
                        setOptions(options.filter((o) => o !== option))
                      }
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Alert severity="info">
              As opções serão exibidas em ordem alfabética junto com as opções
              padrão no sistema.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setDialogOpen(false)}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            style={{ background: "#1361A4", textTransform: "none" }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
